import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { dictionary, Language } from "../../../utils/dictionary";
import { AdTableRow } from "../../../services/meta/metaService";
import CreativeCard, { CreativeMetric } from "./CreativeCard";
import CompetitorSidebar from "./CompetitorSidebar";

interface ProductCreativesViewProps {
  ads: AdTableRow[];
  language: Language;
  onAdClick: (ad: AdTableRow) => void;
}

const METRIC_OPTIONS: { value: CreativeMetric; labelKey: string }[] = [
  { value: "ctr", labelKey: "performance-metric-ctr" },
  { value: "hook_rate", labelKey: "performance-metric-hook-rate" },
  { value: "cost_per_result", labelKey: "performance-metric-cpa" },
  { value: "spend", labelKey: "performance-metric-spend" },
];

const sortAdsByMetric = (ads: AdTableRow[], metric: CreativeMetric): AdTableRow[] => {
  const copy = [...ads];
  copy.sort((a, b) => {
    const av = (a as any)[metric] ?? 0;
    const bv = (b as any)[metric] ?? 0;
    // CPA → ascending (lower is better); others → descending
    if (metric === "cost_per_result") {
      // zero means "no CPA data" — send to the bottom
      if (av === 0 && bv === 0) return 0;
      if (av === 0) return 1;
      if (bv === 0) return -1;
      return av - bv;
    }
    return bv - av;
  });
  return copy;
};

export const ProductCreativesView = ({ ads, language, onAdClick }: ProductCreativesViewProps) => {
  const [metric, setMetric] = useState<CreativeMetric>("ctr");
  const [sortOpen, setSortOpen] = useState(false);

  const sortedAds = useMemo(() => sortAdsByMetric(ads, metric), [ads, metric]);
  const currentLabel = dictionary[METRIC_OPTIONS.find((o) => o.value === metric)!.labelKey]?.[language];

  return (
    <div className="grid gap-6 p-8 bg-[#FAFAFB]" style={{ gridTemplateColumns: "1fr 320px" }}>
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
              {dictionary["performance-your-creatives"]?.[language] ?? "Tus creativos"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {ads.length} {dictionary["campaigns-ads-count"]?.[language] ?? "ads"}
            </p>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setSortOpen((o) => !o)}
              className="inline-flex items-center gap-2 h-9 px-3 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
            >
              {dictionary["performance-sort-by"]?.[language] ?? "Ordenar por"}: <strong className="text-gray-900">{currentLabel}</strong>
              <ChevronDown className="w-3 h-3" />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                {METRIC_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setMetric(opt.value);
                      setSortOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                      metric === opt.value ? "bg-fluxi/5 text-fluxi font-semibold" : "text-gray-700"
                    }`}
                  >
                    {dictionary[opt.labelKey]?.[language] ?? opt.value}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {sortedAds.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-12 text-center">
            <p className="text-sm text-gray-500">
              {dictionary["performance-empty-no-ads-title"]?.[language] ?? "Sin anuncios"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {sortedAds.map((ad, i) => (
              <CreativeCard
                key={ad.ad_id}
                ad={ad}
                rank={i + 1}
                metric={metric}
                language={language}
                onClick={() => onAdClick(ad)}
              />
            ))}
          </div>
        )}
      </div>

      <CompetitorSidebar language={language} metric={metric} />
    </div>
  );
};

export default ProductCreativesView;
